import { Input, Button, Form, Space } from 'antd'
import { HelpMessage } from 'utils/constants'
import CIMSFormItem from 'components/CIMSFormItem'
import { CIMSModalWrapperProps } from 'components/CIMSModal/types'
import UserSelect from 'components/CIMSSelect/common/UserSelect'
import { LabTech, useLabTechsContext } from '../LabTechsContext'
import FormlessModal from 'components/CIMSModal/FormlessModal'
import { useEditLabTechMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { useUserSelectContext } from 'components/CIMSSelect/common/UserSelect/UserSelectContext'
import { useEffect, useState } from 'react'
import { useForm } from 'antd/lib/form/Form'
import { CreateEditValues } from '../types'
import { useCatchDuplicateKeyError } from '../hooks/useCatchDuplicateKeyError'
import TestingLabSelect from 'components/CIMSSelect/common/TestingLabSelect'
import { useTestingLabSelectContext } from 'components/CIMSSelect/common/TestingLabSelect/TestingLabSelectContext'
import { notEqual } from 'shared'
import { errorHandler } from 'hooks/errorHandler'

const EditLabTechModal: React.FC<CIMSModalWrapperProps<LabTech>> = ({
  onClose,
  visible,
  state,
}) => {
  const { toLabeledValue: toUser, fromLabeledValue: fromUser } =
    useUserSelectContext()
  const { toLabeledValues: toTestingLabs, fromLabeledValues: fromTestingLabs } =
    useTestingLabSelectContext()
  const { refetch } = useLabTechsContext()
  const [edit] = useEditLabTechMutation({
    ...DefaultMutationHookOptions,
    ...useCatchDuplicateKeyError(
      'edited',
      ({ editLabTech }) => editLabTech.labTech
    ),
  })
  const [promoted, promote] = useState(false)
  useEffect(() => promote((state && !!state?.user) ?? false), [state])
  const [form] = useForm<CreateEditValues>()
  return (
    <FormlessModal
      ok={false}
      visible={visible}
      cancel={{ fn: onClose }}
      title={`Edit ${state?.name}`}
      afterClose={() => form.resetFields()}
      footer={
        <Space size="middle">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => {
              promote((prev) => !prev)
              form.resetFields()
            }}
          >
            {promoted ? 'Make Generic' : 'Associate a User'}
          </Button>
          <Button type="primary" onClick={() => form.submit()}>
            Ok
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        onFinish={async ({ name, user, testingLabs }) => {
          if (!state?.id) return errorHandler('Lab tech id must be defined')

          const userId = fromUser(user)
          const testingLabIds = fromTestingLabs(testingLabs)
          const differentLabs = notEqual(
            testingLabIds,
            state?.testingLabs?.map(({ id }) => id)
          )
          if (!name && userId === state?.user?.id && !differentLabs)
            return onClose()
          if (
            state?.name !== name ||
            state?.user?.id !== userId ||
            differentLabs
          ) {
            await edit({
              variables: {
                input: {
                  id: state.id,
                  name: name ?? null,
                  userId: userId ?? null,
                  testingLabIds,
                },
              },
            })
            refetch()
          }
          onClose()
        }}
      >
        <CIMSFormItem
          initialValue={state?.name}
          label="Name"
          name="name"
          help={HelpMessage.Name}
          requiredMessage={!promoted ? 'Please enter a name' : undefined}
          description={
            promoted
              ? 'Clear this value to use the name of the User'
              : undefined
          }
        >
          <Input allowClear />
        </CIMSFormItem>
        {promoted && (
          <CIMSFormItem
            initialValue={toUser(state?.user)}
            label="User"
            name="user"
            help={HelpMessage.Concrete.LabTechs.LabTechs.User()}
            requiredMessage="Please select a user"
          >
            <UserSelect />
          </CIMSFormItem>
        )}
        <CIMSFormItem
          label="Testing Labs"
          name="testingLabs"
          initialValue={toTestingLabs(state?.testingLabs)}
        >
          <TestingLabSelect multiple />
        </CIMSFormItem>
      </Form>
    </FormlessModal>
  )
}

export default EditLabTechModal
